import {Editor} from '../editors/Editor.js'
import {createModal} from "../intouch.js";
import {skin} from "../utils/Skin.js";
import {msg} from "@/i18n/Message.ts";

/**
 * @typedef {Object} TabsBlock
 * @property {string[]} tabs - list of tabs in this block
 */

/**
 * @typedef {Object} TabsStructure
 * @property {TabsBlock[]} [RIGHT] - blocks on the right
 * @property {TabsBlock[]} [BOTTOM] - blocks on the top
 */

/**
 * @typedef {Object} TabsMeta
 */

/**
 * @typedef {Object} TabsLayout
 * @property {string} layout - basic layout (ROWS|COLUMNS)
 * @property {TabsStructure} structure
 * @property {TabsMeta} meta
 */


export class SubviewsLayoutEditor extends Editor {

	constructor(parentEditor, fieldPath, obj, member, modal, viewName) {
		super(parentEditor, fieldPath, obj, member);
		this.modal = modal;
		this.viewName = viewName;
	}

	rebuild() {
		this.root = $("<div class=\"subviews-editor\"></div>");
		this.rebuildSwitch();
		this.right = $("<div class=\"sve-container sve-right\"></div>");
		this.bottom = $("<div class=\"sve-container sve-bottom\"></div>");
		this.root.append(this.right);
		this.root.append(this.bottom);
		this.updateScreen();
	}

	rebuildSwitch() {
		this.form = $("<div class=\"sve-form\"></div>");

		const info = $("<div class=\"sve-info\"></div>");
		info.text(msg("sve.info"));
		this.form.append(info);

		this.switch = $("<div class=\"sve-switch\"></div>");
		const rs = $("<label class=\"sve-switch-control\"></label>");
		this.rowsSwitch = $("<input type=\"radio\" name=\"layout\" value='ROWS'>");
		rs.append(this.rowsSwitch);
		this.rowsIcon = $("<img src='i/subview-rows.jsp' class='sve-icons'>");
		rs.append(this.rowsIcon);
		this.switch.append(rs);

		const cs = $("<label class=\"sve-switch-control\"></label>");
		this.colsSwitch = $("<input type=\"radio\" name=\"layout\" value='COLUMNS'>");
		cs.append(this.colsSwitch);
		this.colsIcon = $("<img src='i/subview-cols.jsp' class='sve-icons'>");
		cs.append(this.colsIcon);
		this.switch.append(cs);

		this.form.append(this.switch);

		this.saveButton = $("<button></button>");
		this.saveButton.text(msg("butt.set"));
		this.form.append(this.saveButton);
		this.saveButton.on("click", this.saveDataAndClose.bind(this));

		this.root.append(this.form);

		this.rowsSwitch.on("change", this.layoutChanged.bind(this));
		this.colsSwitch.on("change", this.layoutChanged.bind(this));
	}

	layoutChanged() {
		if (this.rowsSwitch.is(":checked")) {
			this.data.layout = "ROWS";
			this.root.removeClass("sve-columns");
		} else {
			this.data.layout = "COLUMNS";
			this.root.addClass("sve-columns");
		}
	}

	updateScreen() {
		super.updateScreen();
		if (this.data.layout === "ROWS") {
			this.rowsSwitch.prop("checked", true);
			this.root.removeClass("sve-columns");
		} else {
			this.colsSwitch.prop("checked", true);
			this.root.addClass("sve-columns");
		}
		this.updateScreenContainer(this.right, this.data.structure.RIGHT, "RIGHT");
		this.updateScreenContainer(this.bottom, this.data.structure.BOTTOM, "BOTTOM");

	}

	updateScreenContainer(container, blocks, position) {
		container.empty();
		let idx = 0;
		if (blocks) {
			for (let bk of blocks) {
				const tabs = bk.tabs;
				const blockEl = $("<div class=\"sve-block tabs-primary\" data-position='" + position + "' data-idx='" + idx + "'></div>");
				container.append(blockEl);
				if (tabs == null) continue;
				for (let t of tabs) {
					const tabEl = $("<div class=\"sve-tab tab\"></div>");
					tabEl.attr("data-tab", t);
					const m = this.data.meta[t];
					if (!m) {
						const titleEl = $("<span class=\"tab-label\"></span>");
						titleEl.text(t);
						tabEl.append(titleEl);
					} else {
						if (m.icon) {
							let i = m.icon.startsWith("/") ? m.icon.substring(1) : m.icon;
							const iconEl = skin.iconTag(i, tabEl);
							if (iconEl.prop("tagName") === "IMG") iconEl.addClass("tab-icon");
							tabEl.append(iconEl);
						}
						if (m.title) {
							const titleEl = $("<span class=\"tab-label\"></span>");
							titleEl.text(m.title);
							tabEl.append(titleEl);
						}
					}
					blockEl.append(tabEl);
				}
				idx++;
			}
		}
		const extraEmptyBlock = $("<div class=\"sve-block tabs-primary\" data-position='"+position+"' data-idx='"+idx+"'></div>");
		container.append(extraEmptyBlock);
	}

	initSortable() {
		this.currentBlocks = this.root.find(".sve-block");
		this.currentBlocks.sortable({
			connectWith: ".sve-block",
			cursor: "move",
			helper: "clone",
			zIndex: 105000, /* more then modal */
			appendTo: this.modal,
			update: function(event, ui) {
				this.fixNotEnoughEmptyBlocks(this.extractStructure());
			}.bind(this)
		});
	}

	destroySortable() {
		if (this.currentBlocks) {
			this.currentBlocks.sortable("destroy");
			this.currentBlocks = null;
		}
	}

	skinIcons() {
		skin.icon(this.rowsIcon, this.root);
		skin.icon(this.colsIcon, this.root);
		const r = this.root;
		this.root.find(".tab-icon").each(function() {skin.icon($(this), $(this)); });
		this.initSortable();
	}

	extractStructure() {
		const s = {
			RIGHT : [],
			BOTTOM: []
		};
		this.root.find(".sve-block").each(function() {
			const p = $(this).attr("data-position");
			const bks = s[p];
			const idx = parseInt($(this).attr("data-idx"));
			const bk = { tabs: [] };
			bks[idx] = bk;
			$(this).find(".sve-tab").each(function() {
				bk.tabs.push($(this).attr("data-tab"));
			});
		});
		//console.log(JSON.stringify(s));
		return s;
	}

	fixNotEnoughEmptyBlocks(struc) {
		this.fixNotEnoughEmptyBlocksFor(this.right, struc.RIGHT);
		this.fixNotEnoughEmptyBlocksFor(this.bottom, struc.BOTTOM);
	}

	fixNotEnoughEmptyBlocksFor(container, containerBlockArray) {
		let emptyBlocks = 0;
		for (let bk of containerBlockArray) {
			if (bk.tabs.length === 0) emptyBlocks++;
		}
		if (emptyBlocks === 0) {
			// must add empty block
			const containerName = container === this.right ? "RIGHT" : "BOTTOM";
			const blockEl = $("<div class=\"sve-block tabs-primary\" data-position='"+containerName+"' data-idx='"+containerBlockArray.length+"'></div>");
			container.append(blockEl);
			this.destroySortable();
			this.initSortable();
		}
	}

	saveDataAndClose() {
		this.updateData();
		let d = this.data;
		delete d.meta;
		let viewJson = JSON.stringify(d);
		const form = $('<form action="saveSubView.do" method="POST"></form>');

		const dataInput = $("<input name='data' type='hidden'>");
		form.append(dataInput);
		dataInput.val(viewJson);

		const viewInput = $("<input name='view' type='hidden'>");
		form.append(viewInput);
		viewInput.val(this.viewName);

		$('body').append(form);
		form.submit();
	}

	updateData() {
		const s = this.extractStructure();
		this.removeEmptyBlocks(s);
		this.data.structure = s;
	}

	/**
	 * @param {TabsStructure} s
	 */
	removeEmptyBlocks(s) {
		for (let key in s) {
			if (s.hasOwnProperty(key)) {
				const filtered = [];
				for (let bk of s[key]) {
					if (bk.tabs.length > 0) filtered.push(bk);
				}
				s[key] = filtered;
			}
		}
	}

	static launchWhenJQueryUI(layout, viewName) {
		let modal = createModal('subviewsLayoutEditor', 'tabs', msg('sve.form'), true);

		const obj = { data: layout };
		const ed = new SubviewsLayoutEditor(null, "layout", obj, "data", modal, viewName);
		modal.appendToContent(ed.ui);
		ed.skinIcons();

		modal.show();
	}

	static launch(layout, viewName) {
		if (!jQuery.ui) {
			jQuery.getScript("scripts/jquery/jquery-ui.min.js", function() {
				SubviewsLayoutEditor.launchWhenJQueryUI(layout, viewName);
			});
		} else SubviewsLayoutEditor.launchWhenJQueryUI(layout, viewName);
	}
}
